import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, ThemeProvider, Typography, useTheme } from '@mui/material';
import Page from '../../../components/Page';
import apiRequest from '../../../api/request';
import warningTheme, { WD } from '../../warningDashboard/warningTheme';
import WarningLoading from '../../warningDashboard/WarningLoading';
import RetryNotice from '../../warningDashboard/RetryNotice';
import { LEVEL_TONE, OVERVIEW_URL, PAGE_BG } from './utils/constants';
import { addDays, dayDiff, monthGrid, rangeFor, shiftAnchor, todayISO } from './utils/dates';
import { fetchSpan, fmtHours, indexHours, monthStats, projectHoursIn, summariseHours, teamDays } from './utils/hours';
import { projectOptionsOf, projectsInRange } from './utils/tasks';
import WorkloadHeader from './components/WorkloadHeader';
import WorkloadFilters from './components/WorkloadFilters';
import ListStats from './components/ListStats';
import CalendarStats from './components/CalendarStats';
import PersonTable from './components/PersonTable';
import ProjectsPanel from './components/ProjectsPanel';
import LoadLegend from './components/LoadLegend';
import MonthCalendar from './components/MonthCalendar';
import DayPanel from './components/DayPanel';
import HowToRead from './components/HowToRead';
import PersonDrawer from './components/PersonDrawer';
import NotAllowed from './components/NotAllowed';

/**
 * Team Workload - the Technical Department's dashboard (/dashboard/tech-workload), moved over from InteTasks.
 *
 * Who is carrying what across the viewer and everyone beneath them in the reporting line, for whatever dates are chosen. Load is
 * in hours: planned by heads and logged by people, measured against 8h a working day (see utils/hours.js for how a plan becomes
 * hours on a day). The tasks are InteTasks tasks, read by the portal from InteTasks' own tables.
 *
 * Technical Department only: the sidebar item is only added for department 5 (roleSlice), and the server refuses anybody else
 * (403), which this page shows as a notice rather than an error.
 */
export default function TechWorkloadDashboard() {
  const outer = useTheme();
  const theme = useMemo(() => warningTheme(outer), [outer]);

  return (
    <ThemeProvider theme={theme}>
      {/* The portal's own white background, not the Warning Dashboard's cream canvas. */}
      <Box sx={{ bgcolor: PAGE_BG, color: WD.ink, minHeight: '100%', borderRadius: 3, p: { xs: 1, md: 2 }, fontFamily: theme.typography.fontFamily }}>
        <Page title="Team Workload">
          <Workload />
        </Page>
      </Box>
    </ThemeProvider>
  );
}

const TWO_COLUMNS = { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 360px' };

function Workload() {
  const [today, setToday] = useState(todayISO);
  const [view, setView] = useState('list');
  const [preset, setPreset] = useState('week');
  const [anchor, setAnchor] = useState(todayISO);
  /* Set when somebody types dates by hand; a preset click clears it. */
  const [custom, setCustom] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [levelFilter, setLevelFilter] = useState('all');
  const [openPersonId, setOpenPersonId] = useState(null);
  const [selectedDay, setSelectedDay] = useState(todayISO);

  // The calendar always shows a whole month; the list follows the preset or typed dates.
  const range = useMemo(() => {
    if (view === 'calendar') return rangeFor('month', anchor);
    if (custom) return custom;
    return rangeFor(preset, anchor);
  }, [view, preset, anchor, custom]);

  const grid = useMemo(() => monthGrid(anchor), [anchor]);
  const span = useMemo(() => fetchSpan(range, grid, today), [range, grid, today]);

  /* One answer per date span (entries are only sent for the days asked about). What is on screen stays while a new span loads. */
  const [state, setState] = useState({ data: null, error: null, refused: null, loading: true, loadedAt: null });
  const latest = useRef(0);

  const load = useCallback(async (wanted) => {
    const ticket = latest.current + 1;
    latest.current = ticket;
    setState((was) => ({ ...was, loading: true }));
    const res = await apiRequest({ method: 'get', url: OVERVIEW_URL, params: wanted });
    if (latest.current !== ticket) return; // an older span answering late must not replace a newer one
    setToday(todayISO());
    if (res?.status === 'success') {
      setState({ data: res.data, error: null, refused: null, loading: false, loadedAt: new Date() });
    } else if (res?.status === 'failed') {
      setState({ data: null, error: null, refused: res.message, loading: false, loadedAt: null });
    } else {
      setState((was) => ({ ...was, error: res?.message || 'Could not load the workload.', loading: false }));
    }
  }, []);

  useEffect(() => {
    load(span);
  }, [load, span.from, span.to]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(() => load(span), [load, span]);

  const data = state.data;
  /* The server's date decides what counts as past, so logging and the figures agree. */
  const hoursToday = data?.today || today;
  const people = useMemo(() => data?.people || [], [data]);
  const tasks = useMemo(() => data?.tasks || [], [data]);
  const idx = useMemo(() => indexHours(data, hoursToday), [data, hoursToday]);
  const meId = people.find((person) => person.isYou)?.id;

  const projectOptions = useMemo(() => projectOptionsOf(tasks), [tasks]);
  const projects = useMemo(() => projectsInRange({ people, tasks, from: range.from, to: range.to, today }), [people, tasks, range, today]);
  const projectHours = useMemo(() => projectHoursIn(idx, range.from, range.to), [idx, range]);
  const summary = useMemo(() => summariseHours({ people, tasks, idx, from: range.from, to: range.to, projectId }), [people, tasks, idx, range, projectId]);

  const daily = useMemo(() => (view === 'calendar' ? teamDays({ people, idx, days: grid, projectId }) : []), [view, people, idx, grid, projectId]);
  const stats = useMemo(() => monthStats(daily, anchor), [daily, anchor]);

  // Opened from a calendar day, the drawer shows that day rather than the month.
  const drawerRange = view === 'calendar' ? { from: selectedDay, to: selectedDay } : range;
  const drawerSummary = useMemo(
    () => (view === 'calendar' && openPersonId ? summariseHours({ people, tasks, idx, from: selectedDay, to: selectedDay, projectId }) : null),
    [view, openPersonId, people, tasks, idx, selectedDay, projectId],
  );
  const drawerRow = (drawerSummary || summary).rows.find((row) => row.person.id === openPersonId) || null;

  const choosePreset = (key) => {
    setPreset(key);
    setCustom(null);
    if (custom) setAnchor(custom.from);
  };

  const step = (direction) => {
    if (custom && view === 'list') {
      const length = dayDiff(custom.from, custom.to) + 1;
      setCustom({ from: addDays(custom.from, length * direction), to: addDays(custom.to, length * direction) });
      return;
    }
    setAnchor((was) => shiftAnchor(view === 'calendar' ? 'month' : preset, was, direction));
  };

  const typeDate = (edge, value) => {
    if (!value) return;
    const next = { ...range, [edge]: value };
    if (next.from > next.to) {
      if (edge === 'from') next.to = value;
      else next.from = value;
    }
    setCustom(next);
    setAnchor(next.from);
  };

  const goToday = () => {
    setCustom(null);
    setAnchor(today);
    setSelectedDay(today);
  };

  const openDay = (day) => {
    setView('list');
    setPreset('day');
    setCustom(null);
    setAnchor(day);
  };

  const switchView = (key) => {
    setView(key);
    if (key === 'calendar') {
      const base = custom ? custom.from : anchor;
      setAnchor(base);
      setSelectedDay(base.slice(0, 7) === today.slice(0, 7) ? today : base);
    }
  };

  if (state.refused) return <NotAllowed message={state.refused} />;

  return (
    <Box maxWidth={2000}>
      <WorkloadHeader
        range={range}
        people={people.length}
        capacityPerDay={idx.capacityPerDay}
        projectName={projectOptions.find((option) => option.id === projectId)?.name}
        loading={state.loading}
        loadedAt={state.loadedAt}
        onRefresh={reload}
        onLogMyTime={meId ? () => setOpenPersonId(meId) : null}
      />

      <WorkloadFilters
        view={view}
        preset={custom ? null : preset}
        range={range}
        projectId={projectId}
        projectOptions={projectOptions}
        onView={switchView}
        onPreset={choosePreset}
        onStep={step}
        onDate={typeDate}
        onToday={goToday}
        onProject={setProjectId}
      />

      {state.error && <RetryNotice message={state.error} onRetry={reload} sx={{ mt: 2 }} />}

      {data && summary.totals.needsHours > 0 && (
        <Alert severity="warning" sx={{ mt: 2, bgcolor: LEVEL_TONE.atCapacity.bg, color: LEVEL_TONE.atCapacity.text }}>
          {summary.totals.needsHours} task{summary.totals.needsHours === 1 ? '' : 's'} in these dates {summary.totals.needsHours === 1 ? 'has' : 'have'} no hours
          set yet, so {summary.totals.needsHours === 1 ? 'it adds' : 'they add'} nothing to the load. Open a person to set them.
        </Alert>
      )}

      {!data ? (
        !state.error && (
          <Box sx={{ mt: 3 }}>
            <WarningLoading message="Loading your team's workload…" />
          </Box>
        )
      ) : view === 'list' ? (
        <>
          <ListStats summary={summary} />
          <Box display="grid" gridTemplateColumns={TWO_COLUMNS} gap={2.5} sx={{ mt: 3 }}>
            <PersonTable summary={summary} levelFilter={levelFilter} onLevelFilter={setLevelFilter} onOpen={setOpenPersonId} />
            <Box sx={{ display: 'grid', gap: 2.5, alignContent: 'start' }}>
              <ProjectsPanel
                projects={projects}
                hoursOf={(id) => projectHours.get(id) || 0}
                selected={projectId}
                onSelect={(id) => setProjectId((was) => (was === id ? null : id))}
              />
              <LoadLegend capacityPerDay={idx.capacityPerDay} />
              {summary.totals.paused > 0 && (
                <Typography sx={{ px: 0.5, fontSize: 12, color: WD.inkFaint }}>
                  {fmtHours(summary.totals.paused)} of planned work is paused on tasks that are on hold.
                </Typography>
              )}
            </Box>
          </Box>
        </>
      ) : (
        <>
          <CalendarStats stats={stats} />
          <Box display="grid" gridTemplateColumns={TWO_COLUMNS} gap={2.5} sx={{ mt: 3 }}>
            <MonthCalendar
              anchor={anchor}
              daily={daily}
              today={today}
              selected={selectedDay}
              onSelect={setSelectedDay}
              onPrev={() => step(-1)}
              onNext={() => step(1)}
              onToday={goToday}
            />
            <Box sx={{ display: 'grid', gap: 2.5, alignContent: 'start' }}>
              <DayPanel day={selectedDay} people={people} tasks={tasks} idx={idx} today={hoursToday} projectId={projectId} onOpenPerson={setOpenPersonId} onOpenDay={openDay} />
              <HowToRead capacityPerDay={idx.capacityPerDay} />
            </Box>
          </Box>
        </>
      )}

      <PersonDrawer
        row={drawerRow}
        idx={idx}
        from={drawerRange.from}
        to={drawerRange.to}
        today={hoursToday}
        projectId={projectId}
        canLog={Boolean(drawerRow && (drawerRow.person.isYou || idx.canPlan.has(drawerRow.person.id)))}
        onChanged={reload}
        onClose={() => setOpenPersonId(null)}
      />
    </Box>
  );
}
